import React, { useEffect, useMemo, useState } from "react";

interface NavbarMovementProps {
  options: string[];
  onSelect?: (selected: string) => void;
  initialSelected?: string;
  compact?: boolean;
  color?: string;
  marginTop?: number;
  marginBottom?: number;
}

const NavbarMovement: React.FC<NavbarMovementProps> = ({
  options,
  onSelect,
  initialSelected,
  compact = true,
  color = "#0f333b",
  marginTop,
  marginBottom,
}) => {
  const PRIMARY_COLOR = color || "#0f333b";

  const [selected, setSelected] = useState<string>(
    initialSelected || options[0] || ""
  );

  useEffect(() => {
    if (initialSelected) {
      setSelected(initialSelected);
      return;
    }

    if (!selected && options.length > 0) {
      setSelected(options[0]);
    }

    if (selected && options.length > 0 && !options.includes(selected)) {
      setSelected(options[0]);
    }
  }, [initialSelected, options, selected]);

  const handleSelect = (option: string) => {
    setSelected(option);
    onSelect?.(option);
  };

  const containerStyle = useMemo<React.CSSProperties>(
    () => ({
      width: "100%",
      backgroundColor: "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: compact ? 6 : 8,
      fontFamily:
        "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      marginTop: marginTop ?? (compact ? 48 : 58),
      marginBottom: marginBottom ?? (compact ? 6 : 8),
      padding: compact ? "4px 8px" : "6px 10px",
      boxSizing: "border-box",
    }),
    [compact, marginTop, marginBottom]
  );

  const optionStyle = useMemo<React.CSSProperties>(
    () => ({
      minHeight: compact ? 26 : 30,
      padding: compact ? "0 10px" : "0 14px",
      fontSize: compact ? 11.5 : 12.5,
      fontWeight: 600,
      letterSpacing: "0.01em",
      color: PRIMARY_COLOR,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 0,
      backgroundColor: "transparent",
      border: `1px solid ${PRIMARY_COLOR}`,
      boxSizing: "border-box",
      transition:
        "background-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease, transform 0.12s ease",
      userSelect: "none",
      whiteSpace: "nowrap",
      lineHeight: 1,
      outline: "none",
      fontFamily: "inherit",
    }),
    [PRIMARY_COLOR, compact]
  );

  const selectedStyle = useMemo<React.CSSProperties>(
    () => ({
      backgroundColor: PRIMARY_COLOR,
      color: "#FFFFFF",
      boxShadow: "0 1px 4px rgba(0,0,0,0.16)",
    }),
    [PRIMARY_COLOR]
  );

  const hoverColor = "rgba(15, 51, 59, 0.06)";

  return (
    <div style={containerStyle}>
      {options.map((option) => {
        const isSelected = selected === option;

        return (
          <button
            key={option}
            type="button"
            onClick={() => handleSelect(option)}
            style={{
              ...optionStyle,
              ...(isSelected ? selectedStyle : {}),
            }}
            onMouseEnter={(e) => {
              if (isSelected) return;

              e.currentTarget.style.backgroundColor = hoverColor;
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              if (isSelected) return;

              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};

export default NavbarMovement;
